export interface ErrorEntry {
  level: 'error' | 'warn'
  message: string
  timestamp: number
}

const MAX_ENTRIES = 50
const MAX_MSG_LEN = 2000
const STORAGE_KEY = 'isqroll_error_buffer'

let buffer: ErrorEntry[] = loadPersisted()
let installed = false

function loadPersisted(): ErrorEntry[] {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed.slice(-MAX_ENTRIES) : []
  } catch {
    return []
  }
}

function persist(): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(buffer))
  } catch {
    /* storage full / unavailable — buffer stays in memory */
  }
}

function serialise(args: unknown[]): string {
  return args
    .map((a) => {
      if (typeof a === 'string') return a
      if (a instanceof Error) return a.stack || a.message
      try {
        return JSON.stringify(a)
      } catch {
        return String(a)
      }
    })
    .join(' ')
}

function push(level: ErrorEntry['level'], args: unknown[]): void {
  try {
    const message = serialise(args).slice(0, MAX_MSG_LEN)
    buffer.push({ level, message, timestamp: Date.now() })
    if (buffer.length > MAX_ENTRIES) buffer = buffer.slice(-MAX_ENTRIES)
    persist()
  } catch {
    /* a logging shim must never throw */
  }
}

export function getErrorBuffer(): ErrorEntry[] {
  return [...buffer]
}

export function clearErrorBuffer(): void {
  buffer = []
  try {
    sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    /* ignore */
  }
}

export function installErrorBuffer(): void {
  if (installed) return
  installed = true

  const origError = console.error.bind(console)
  const origWarn = console.warn.bind(console)
  console.error = (...args: unknown[]) => {
    push('error', args)
    origError(...args)
  }
  console.warn = (...args: unknown[]) => {
    push('warn', args)
    origWarn(...args)
  }

  if (typeof window !== 'undefined') {
    window.addEventListener('error', (e) => {
      push('error', [`Uncaught: ${e.message} @ ${e.filename}:${e.lineno}:${e.colno}`])
    })
    window.addEventListener('unhandledrejection', (e) => {
      const reason = (e.reason && (e.reason.stack || e.reason.message)) || String(e.reason)
      push('error', [`Unhandled rejection: ${reason}`])
    })
  }
}
